import { z } from "zod";

export const API_LIMITS = {
  appVersion: 64,
  assignmentItems: 19,
  assignmentTitle: 120,
  className: 80,
  contentVersion: 64,
  courseCode: 24,
  eventBatch: 100,
  eventDurationMs: 86_400_000,
  eventPayloadEntries: 20,
  eventPayloadKey: 64,
  eventPayloadString: 500,
  feedback: 2_000,
  formalTasks: 19,
  inviteCode: 32,
  itemId: 64,
  major: 100,
  name: 50,
  password: 128,
  problemDetail: 1_000,
  problemErrors: 50,
  problemTitle: 160,
  recoveryCode: 64,
  studentId: 32,
  taskItems: 100,
  taskStem: 2_000,
  taskTitle: 160,
  username: 64,
} as const;

export const uuidSchema = z.uuid();
export const timestampSchema = z.string().datetime({ offset: true });

export const contentVersionSchema = z
  .string()
  .trim()
  .min(1)
  .max(API_LIMITS.contentVersion)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);

export const appVersionSchema = z
  .string()
  .trim()
  .min(1)
  .max(API_LIMITS.appVersion)
  .regex(/^[A-Za-z0-9][A-Za-z0-9.+_-]*$/);

export const taskIdSchema = z.string().regex(/^T(?:0[1-9]|1[0-9])$/);
export const SCENE_IDS = [
  "M1S1",
  "M1S2",
  "M2S1",
  "M2S2",
  "M2S3",
  "M3S1",
  "M3S2",
  "M3S3",
  "M3S4",
  "M4S1",
  "M4S2",
  "M4S3",
  "M5S1",
  "M5S2",
  "M5S3",
  "M6S1",
  "M6S2",
  "M6S3",
] as const;
export const sceneIdSchema = z.enum(SCENE_IDS);
export const moduleIdSchema = z.string().regex(/^M[1-6]$/);

export const itemIdSchema = z
  .string()
  .min(1)
  .max(API_LIMITS.itemId)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/);

export const nameSchema = z.string().trim().min(1).max(API_LIMITS.name);
export const majorSchema = z.string().trim().min(1).max(API_LIMITS.major);
export const studentIdSchema = z
  .string()
  .trim()
  .min(3)
  .max(API_LIMITS.studentId)
  .regex(/^[A-Za-z0-9_-]+$/);

export const usernameSchema = z
  .string()
  .trim()
  .min(3)
  .max(API_LIMITS.username)
  .regex(/^[A-Za-z0-9._@-]+$/);

export const courseCodeSchema = z
  .string()
  .trim()
  .min(3)
  .max(API_LIMITS.courseCode)
  .regex(/^[A-Za-z0-9-]+$/)
  .transform((value) => value.toUpperCase());

export const inviteCodeSchema = z
  .string()
  .trim()
  .min(6)
  .max(API_LIMITS.inviteCode)
  .regex(/^[A-Za-z0-9-]+$/)
  .transform((value) => value.toUpperCase());

export const csrfTokenSchema = z
  .string()
  .min(32)
  .max(256)
  .regex(/^[A-Za-z0-9_-]+$/);

export const recoveryCodeSchema = z
  .string()
  .trim()
  .max(API_LIMITS.recoveryCode)
  .regex(/^REC-[A-Za-z0-9_-]{43}$/);

export const idempotencyKeySchema = z
  .string()
  .min(16)
  .max(128)
  .regex(/^[A-Za-z0-9._:-]+$/);

export const reviewStateSchema = z.enum(["seed", "pending_review", "approved", "blocked"]);
export const assignmentVersionSchema = z.number().int().min(1).max(2_147_483_647);

export type ContentVersion = z.infer<typeof contentVersionSchema>;
export type TaskId = z.infer<typeof taskIdSchema>;
export type SceneId = z.infer<typeof sceneIdSchema>;
export type ModuleId = z.infer<typeof moduleIdSchema>;
